import React from 'react'
import {
  BoxV2,
  CheckBoxFieldv2,
  RadioButtonType,
  RadioButtonsV2,
  ExternalLink,
} from '@labrav/react-components'
import { RequirementsAction } from '../../common/components/RequirementsAction/RequirementsAction'
import { awsRequirementsText } from '../../common/utils/constants'
import { CloudType } from '../../types/index'
import { FormikProps, useFormikContext } from 'formik'
import {
  FlyOutOnboardingTerm,
  FlyoutOnboardingSectionName,
  Sections,
} from '../../modules/flyOutOnboarding/reducer'
import { useStyles } from './FlyOutOnboardingAdhere.styles'
import clsx from 'clsx'
import { Typography } from '@mui/material'
import { get, isEmpty } from 'lodash'
import { FormLockedType } from '../../types/FormLockedType'
export interface FlyOutOnboardingAdhereProps {
  requirementsLink: string
  sectionName: FlyoutOnboardingSectionName
}

export const FlyOutOnboardingAdhere: React.FC<
  FlyOutOnboardingAdhereProps & FormLockedType
> = ({ sectionName, requirementsLink, isFormLocked }) => {
  const { values, errors, touched, handleChange, setFieldValue } =
    useFormikContext() as FormikProps<Sections>
  const termsAndConditions = get(
    values?.termsAndConditions,
    `${sectionName}`,
    []
  ) as FlyOutOnboardingTerm[]

  const termsAndConditionsError = get(
    errors?.termsAndConditions,
    `${sectionName}`,
    []
  ) as FlyOutOnboardingTerm[]

  const termsAndConditionsTouched = get(
    touched?.termsAndConditions,
    `${sectionName}`,
    []
  ) as FlyOutOnboardingTerm[]

  const handleRadioChanges =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue(
        `termsAndConditions.${sectionName}.${index}.value`,
        event.target.value === 'Yes'
      )
      if (
        (isEmpty(event.target.value) || event.target.value === 'No') &&
        termsAndConditions[index].subTermsWhenTrue
      ) {
        setFieldValue(
          `termsAndConditions.${sectionName}.${index}.subTermsWhenTrue.value`,
          false
        )
      }
    }

  const { classes } = useStyles()
  return !isEmpty(termsAndConditions) ? (
    <BoxV2
      heading={
        sectionName === FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS ? (
          <Typography className={classes.boxHeading}>
            I agree to have completed the following:
          </Typography>
        ) : (
          <Typography className={classes.boxHeading}>
            I acknowledge and agree to follow these AWS enforced guidelines -
          </Typography>
        )
      }
      secondaryActions={
        <RequirementsAction
          link={requirementsLink}
          linkText={awsRequirementsText}
          cloudType={CloudType.AWS}
        />
      }
    >
      <div
        className={clsx(classes.labelText, classes.container)}
        data-testid={`field-${sectionName}-adhere`}
      >
        {termsAndConditions.map((term, index) => (
          <div>
            {term.type === 'checkbox' ? (
              <CheckBoxFieldv2
                keyName={`termsAndConditions.${sectionName}.${index}.value`}
                title={
                  <div>
                    {parseLabel(term.label || '').map((part, idx) => (
                      <React.Fragment key={idx}>{part}</React.Fragment>
                    ))}
                  </div>
                }
                value={term.value}
                onChangeBox={handleChange}
                disabled={isFormLocked}
              />
            ) : (
              <div className={classes.radioContainer}>
                <Typography className={classes.radioLabel}>
                  {term.label}
                </Typography>
                <div className={classes.textWidth}>
                  <RadioButtonsV2
                    name={`termsAndConditions.${sectionName}.${index}.value`}
                    checkedValue={{
                      key: term.value ? 'Yes' : 'No',
                      value: term.value ? 'Yes' : 'No',
                    }}
                    handleChange={handleRadioChanges(index)}
                    radioButtonMappings={[
                      {
                        key: 'Yes',
                        label: 'Yes',
                        type: RadioButtonType.STANDARD,
                        disabled: isFormLocked,
                      },
                      {
                        key: 'No',
                        label: 'No',
                        type: RadioButtonType.STANDARD,
                        disabled: isFormLocked,
                      },
                    ]}
                  />
                </div>
                <div>
                  {term.subTermsWhenTrue && term.value ? (
                    <div
                      className={classes.subTerms}
                      data-testid={`subTerms-${index}`}
                    >
                      <CheckBoxFieldv2
                        keyName={`termsAndConditions.${sectionName}.${index}.subTermsWhenTrue.value`}
                        title={term.subTermsWhenTrue.label}
                        value={term.subTermsWhenTrue.value}
                        onChangeBox={handleChange}
                        disabled={isFormLocked}
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {termsAndConditionsTouched?.length > 0 &&
        termsAndConditionsError?.length > 0 && (
          <div className={classes.errorMessage}>
            {termsAndConditionsError?.find(item => item && item?.value)?.value}
          </div>
        )}
    </BoxV2>
  ) : (
    <></>
  )
}
export const parseLabel = (label: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = []
  // eslint-disable-next-line no-useless-escape
  const regex = /(?:\[([^\]]+)\]\((https?:\/\/[^\)]+)\))|<br>/g
  let lastIndex = 0
  const { classes } = useStyles()

  label.replace(regex, (match, text, url, index) => {
    parts.push(label.substring(lastIndex, index))
    parts.push(
      match === '<br>' ? (
        <br key={index} />
      ) : (
        <ExternalLink
          href={url}
          key={url}
          className={classes.externalLinkContainer}
        >
          {text}
        </ExternalLink>
      )
    )
    lastIndex = index + match.length
    return ''
  })

  parts.push(label.substring(lastIndex))
  return parts
}
